/*
 * Colour themes (Settings > Appearance). "engram" is the stylesheet's own palette (styles/theme.css);
 * every other theme sets the same CSS variables inline on <html>, with a light and a dark version, so it
 * works with System / Light / Dark. Each theme keeps Engram's colour roles (create, accent = select/links,
 * task, you, scratch pad); only the hues change. Synced as setting `appearance.palette`, so a pick wins on every device.
 */

export type PaletteId = "engram" | "evernote" | "apple" | "notion" | "bear" | "obsidian" | "daylight" | "sage" | "midnight";

interface Tones {
  sidebar: string; list: string; paper: string; hover: string; line: string; chip: string; input: string;
  ink: string; ink2: string; muted: string; faint: string;
  create: string; createHover: string; createInk: string; createText: string; createDeep: string;
  accent: string; accentDeep: string; accentLine: string; accentSoft: string; accentText: string;
  task: string; taskDeep: string; taskSoft: string; you: string;
  scratch: string; scratchRule: string;
  /** --clay (caret, active nav icon, tab underline) when the create colour is too light on the paper. */
  clay?: string;
}

export interface Palette {
  id: PaletteId;
  name: string;
  /** One line for the picker. */
  note: string;
  light?: Tones;
  dark?: Tones;
}

const LIGHT_EXTRA = {
  "--danger": "#c93434", "--star": "#e9b400", "--hit": "#ffe46b", "--hit-ink": "#211c18",
  "--overlay": "rgba(20, 20, 24, 0.28)", "--shadow": "0 8px 28px rgba(20, 20, 30, 0.14), 0 1px 3px rgba(20, 20, 30, 0.1)",
  "--cb-gray-1": "#efefef", "--cb-yellow-1": "#fbf0c4", "--cb-orange-1": "#fbe1cc", "--cb-red-1": "#f8d7d3", "--cb-purple-1": "#ece0f7", "--cb-blue-1": "#dbe6f7", "--cb-green-1": "#dcefd8",
  "--cb-gray-2": "#dadada", "--cb-yellow-2": "#f5dc7a", "--cb-orange-2": "#f5bd8f", "--cb-red-2": "#efa7a0", "--cb-purple-2": "#d2b8ef", "--cb-blue-2": "#a9c5ee", "--cb-green-2": "#acd9a3",
};
const DARK_EXTRA = {
  "--danger": "#f06a6a", "--star": "#ffdf3c", "--hit": "#8a7414", "--hit-ink": "#fff8dc",
  "--overlay": "rgba(0, 0, 0, 0.5)", "--shadow": "0 10px 32px rgba(0, 0, 0, 0.5), 0 1px 3px rgba(0, 0, 0, 0.4)",
  "--cb-gray-1": "#2c2c2c", "--cb-yellow-1": "#3a3214", "--cb-orange-1": "#3c2614", "--cb-red-1": "#3c1d1b", "--cb-purple-1": "#2f2240", "--cb-blue-1": "#1d2a40", "--cb-green-1": "#1c3320",
  "--cb-gray-2": "#444444", "--cb-yellow-2": "#6b5a17", "--cb-orange-2": "#6e4119", "--cb-red-2": "#6e2c27", "--cb-purple-2": "#4f3570", "--cb-blue-2": "#2f4a73", "--cb-green-2": "#2f5a33",
};

export const PALETTES: Palette[] = [
  { id: "engram", name: "Engram", note: "Beige paper, clay and warm browns" },
  {
    id: "evernote",
    name: "Evernote",
    note: "Neutral greys, Evernote green to create, periwinkle to select",
    light: {
      sidebar: "#f4f4f4", list: "#fafafa", paper: "#ffffff", hover: "#ebebeb", line: "#e6e6e6", chip: "#f0f0f0", input: "#ffffff",
      ink: "#333333", ink2: "#4d4d4d", muted: "#737373", faint: "#a6a6a6",
      create: "#008925", createHover: "#007c22", createInk: "#ffffff", createText: "#00852a", createDeep: "#007f23",
      accent: "#4f5fe0", accentDeep: "#3245bb", accentLine: "#5a6be6", accentSoft: "#e3e6fc", accentText: "#3a4bcb",
      task: "#8f4fe0", taskDeep: "#7a3fc2", taskSoft: "#f0e6fc", you: "#d73e20",
      scratch: "#fff5cc", scratchRule: "#f2e3a0",
    },
    dark: {
      sidebar: "#262626", list: "#262626", paper: "#141414", hover: "#333333", line: "#333333", chip: "#333333", input: "#1f1f1f",
      ink: "#f9f8f8", ink2: "#e0e0e0", muted: "#a6a6a6", faint: "#737373",
      create: "#4dc869", createHover: "#62d47c", createInk: "#141414", createText: "#4dc869", createDeep: "#1e7a35",
      accent: "#7c8eff", accentDeep: "#3245bb", accentLine: "#4457cd", accentSoft: "#262e5c", accentText: "#9aa7ff",
      task: "#bd85f6", taskDeep: "#9b5de5", taskSoft: "#3a2b4d", you: "#d73e20",
      scratch: "#33301c", scratchRule: "#45412a",
    },
  },
  {
    id: "apple",
    name: "Apple Notes",
    note: "Clean system greys with Notes yellow",
    light: {
      sidebar: "#ececec", list: "#ffffff", paper: "#ffffff", hover: "#e4e4e4", line: "#e5e5ea", chip: "#f2f2f7", input: "#ffffff",
      ink: "#1d1d1f", ink2: "#3a3a3c", muted: "#86868b", faint: "#aeaeb2",
      create: "#ffcc00", createHover: "#ffd60a", createInk: "#1d1d1f", createText: "#9a6f00", createDeep: "#8a6500",
      accent: "#bc8d00", accentDeep: "#8a6500", accentLine: "#b28f00", accentSoft: "#fff0b3", accentText: "#9a6f00",
      task: "#e0a800", taskDeep: "#b88900", taskSoft: "#fff3c4", you: "#636366",
      scratch: "#fff8d6", scratchRule: "#f0e2a8",
      clay: "#9a6f00",
    },
    dark: {
      sidebar: "#2b2b2d", list: "#1e1e1e", paper: "#1e1e1e", hover: "#3a3a3c", line: "#38383a", chip: "#2c2c2e", input: "#2c2c2e",
      ink: "#f5f5f7", ink2: "#d1d1d6", muted: "#98989d", faint: "#6e6e73",
      create: "#ffd60a", createHover: "#ffe04d", createInk: "#1c1c1e", createText: "#ffd60a", createDeep: "#8a6d00",
      accent: "#ffd60a", accentDeep: "#8a6d00", accentLine: "#ffd60a", accentSoft: "#4a3d00", accentText: "#ffd60a",
      task: "#ffd60a", taskDeep: "#e0b800", taskSoft: "#4a3d00", you: "#636366",
      scratch: "#3a3520", scratchRule: "#4a4430",
    },
  },
  {
    id: "notion",
    name: "Notion",
    note: "Almost monochrome, soft warm greys, one blue",
    light: {
      sidebar: "#f7f7f5", list: "#ffffff", paper: "#ffffff", hover: "#efefee", line: "#e9e9e7", chip: "#f1f1ef", input: "#ffffff",
      ink: "#37352f", ink2: "#4a4843", muted: "#787774", faint: "#a5a29a",
      create: "#1c78d2", createHover: "#1a6ec0", createInk: "#ffffff", createText: "#1f6fc5", createDeep: "#0b6bcb",
      accent: "#2383e2", accentDeep: "#0b6bcb", accentLine: "#2383e2", accentSoft: "#e7f3f8", accentText: "#1f6fc5",
      task: "#2383e2", taskDeep: "#0b6bcb", taskSoft: "#e7f3f8", you: "#b5600b",
      scratch: "#fbf3db", scratchRule: "#f1e3b8",
    },
    dark: {
      sidebar: "#202020", list: "#191919", paper: "#191919", hover: "#2c2c2c", line: "#2f2f2f", chip: "#2f2f2f", input: "#252525",
      ink: "#d4d4d4", ink2: "#bfbfbf", muted: "#9b9b9b", faint: "#6b6b6b",
      create: "#1c78d2", createHover: "#1a6ec0", createInk: "#ffffff", createText: "#529cca", createDeep: "#0b6bcb",
      accent: "#2383e2", accentDeep: "#0b6bcb", accentLine: "#2383e2", accentSoft: "#1f3a50", accentText: "#529cca",
      task: "#529cca", taskDeep: "#2383e2", taskSoft: "#1f3a50", you: "#b5600b",
      scratch: "#39331f", scratchRule: "#4a432c",
    },
  },
  {
    id: "bear",
    name: "Bear",
    note: "Graphite with Bear's red",
    light: {
      sidebar: "#f2f2f2", list: "#fafafa", paper: "#ffffff", hover: "#e8e8e8", line: "#e3e3e3", chip: "#f0f0f0", input: "#ffffff",
      ink: "#444444", ink2: "#555555", muted: "#8a8a8a", faint: "#b3b3b3",
      create: "#d9393c", createHover: "#d0282b", createInk: "#ffffff", createText: "#c23538", createDeep: "#b83235",
      accent: "#dd4c4f", accentDeep: "#b83235", accentLine: "#dd4c4f", accentSoft: "#fbe3e3", accentText: "#c23538",
      task: "#dd4c4f", taskDeep: "#b83235", taskSoft: "#fbe3e3", you: "#d9393c",
      scratch: "#fff6d6", scratchRule: "#f0e3ae",
    },
    dark: {
      sidebar: "#252629", list: "#2b2c2f", paper: "#202124", hover: "#35363a", line: "#3a3b3f", chip: "#333438", input: "#2b2c2f",
      ink: "#dddddd", ink2: "#c8c8c8", muted: "#8e8e93", faint: "#666669",
      create: "#e03127", createHover: "#d0271e", createInk: "#ffffff", createText: "#f06a63", createDeep: "#b83235",
      accent: "#e5534b", accentDeep: "#b83235", accentLine: "#e5534b", accentSoft: "#4a2426", accentText: "#f07d77",
      task: "#f06a63", taskDeep: "#e5534b", taskSoft: "#4a2426", you: "#e03127",
      scratch: "#34301f", scratchRule: "#45402b",
    },
  },
  {
    id: "obsidian",
    name: "Obsidian",
    note: "Charcoal with Obsidian purple",
    light: {
      sidebar: "#f6f6f6", list: "#f6f6f6", paper: "#ffffff", hover: "#ebebeb", line: "#e3e3e3", chip: "#f0f0f0", input: "#ffffff",
      ink: "#222222", ink2: "#3b3b3b", muted: "#5c5c5c", faint: "#ababab",
      create: "#8555f5", createHover: "#7944f4", createInk: "#ffffff", createText: "#7044e0", createDeep: "#6a3fd8",
      accent: "#8a5cf5", accentDeep: "#6a3fd8", accentLine: "#8a5cf5", accentSoft: "#ede6fe", accentText: "#7044e0",
      task: "#8a5cf5", taskDeep: "#6a3fd8", taskSoft: "#ede6fe", you: "#8555f5",
      scratch: "#fff7d4", scratchRule: "#efe2ab",
    },
    dark: {
      sidebar: "#262626", list: "#262626", paper: "#1e1e1e", hover: "#363636", line: "#363636", chip: "#2e2e2e", input: "#2a2a2a",
      ink: "#dadada", ink2: "#c4c4c4", muted: "#999999", faint: "#666666",
      create: "#8555f5", createHover: "#7944f4", createInk: "#ffffff", createText: "#a68af9", createDeep: "#6a3fd8",
      accent: "#a88bfa", accentDeep: "#6a3fd8", accentLine: "#8a5cf5", accentSoft: "#3a2e5c", accentText: "#b9a2fb",
      task: "#a88bfa", taskDeep: "#8a5cf5", taskSoft: "#3a2e5c", you: "#8555f5",
      scratch: "#33301f", scratchRule: "#45402b",
    },
  },
  {
    id: "daylight",
    name: "Daylight",
    note: "Engram, brightened: crisp neutrals, clay to create, clear blue",
    light: {
      sidebar: "#f5f2ed", list: "#fbfaf8", paper: "#ffffff", hover: "#ece7df", line: "#e8e2d9", chip: "#f2ede6", input: "#ffffff",
      ink: "#1e1b18", ink2: "#3a3530", muted: "#6b645c", faint: "#a39b91",
      create: "#ef7a50", createHover: "#e86c40", createInk: "#1e1b18", createText: "#b4502b", createDeep: "#b5532f",
      accent: "#3b6ef5", accentDeep: "#2f5bd6", accentLine: "#5b87f7", accentSoft: "#e1eafe", accentText: "#2f5bd6",
      task: "#8a4fd0", taskDeep: "#7a3fc2", taskSoft: "#efe3fb", you: "#dc2f6b",
      scratch: "#fff4c7", scratchRule: "#f2e3a8",
      clay: "#b4502b",
    },
    dark: {
      sidebar: "#1c1c1f", list: "#202024", paper: "#16161a", hover: "#2a2a2f", line: "#2e2e34", chip: "#2a2a30", input: "#1e1e22",
      ink: "#f2f2f5", ink2: "#d6d6dc", muted: "#9c9ca6", faint: "#6c6c75",
      create: "#f28a63", createHover: "#f59b78", createInk: "#16161a", createText: "#f59b78", createDeep: "#b5532f",
      accent: "#7b9cff", accentDeep: "#3f62d6", accentLine: "#5a7df0", accentSoft: "#232d4d", accentText: "#8faaff",
      task: "#c08cf0", taskDeep: "#9a62d6", taskSoft: "#3a2b4d", you: "#dc2f6b",
      scratch: "#353019", scratchRule: "#4a4326",
    },
  },
  {
    id: "sage",
    name: "Sage",
    note: "Calm and fresh: soft greens, teal links",
    light: {
      sidebar: "#eef3ef", list: "#f6f9f7", paper: "#ffffff", hover: "#e2eae4", line: "#dce5de", chip: "#e9f0eb", input: "#ffffff",
      ink: "#17211c", ink2: "#2e3b34", muted: "#5e6b64", faint: "#9aa59f",
      create: "#3cb48a", createHover: "#33a07a", createInk: "#0f1a15", createText: "#1f7a5a", createDeep: "#1f7a55",
      accent: "#2a8c9e", accentDeep: "#1f6f7e", accentLine: "#3aa0b2", accentSoft: "#ddf0f2", accentText: "#1f7484",
      task: "#7a5bd0", taskDeep: "#6646bf", taskSoft: "#ebe5fa", you: "#d53d5b",
      scratch: "#fff6d1", scratchRule: "#efe2aa",
      clay: "#1f7a5a",
    },
    dark: {
      sidebar: "#17201c", list: "#1b2621", paper: "#131b17", hover: "#24312b", line: "#2a3731", chip: "#22302a", input: "#18221e",
      ink: "#e7f0eb", ink2: "#cad7d0", muted: "#93a39b", faint: "#66766e",
      create: "#5ccb98", createHover: "#74d6a9", createInk: "#0f1a15", createText: "#74d6a9", createDeep: "#1f7a55",
      accent: "#5cc2d1", accentDeep: "#237c8a", accentLine: "#3c9dac", accentSoft: "#1d3a3f", accentText: "#7ad0dc",
      task: "#b9a0f2", taskDeep: "#9478e0", taskSoft: "#2f2945", you: "#d7395a",
      scratch: "#2a2c1b", scratchRule: "#3b3d27",
    },
  },
  {
    id: "midnight",
    name: "Midnight",
    note: "Deep blue-black, vivid aqua and blue",
    light: {
      sidebar: "#eef1f7", list: "#f6f8fb", paper: "#ffffff", hover: "#e3e8f1", line: "#dfe4ee", chip: "#eaeef5", input: "#ffffff",
      ink: "#141a26", ink2: "#2c3445", muted: "#5d6779", faint: "#9aa3b4",
      create: "#2ec4b6", createHover: "#25b0a3", createInk: "#0e1117", createText: "#14806f", createDeep: "#147a6e",
      accent: "#3e63dd", accentDeep: "#2f4fc0", accentLine: "#5b7ce6", accentSoft: "#e3e9fb", accentText: "#2f4fc0",
      task: "#7c4ddb", taskDeep: "#6a3cc8", taskSoft: "#ece4fb", you: "#dc2f6b",
      scratch: "#fff5cf", scratchRule: "#efe1a6",
      clay: "#14806f",
    },
    dark: {
      sidebar: "#10131a", list: "#141821", paper: "#0e1117", hover: "#1c2230", line: "#222a38", chip: "#1c2230", input: "#121620",
      ink: "#e8ecf5", ink2: "#c9d0de", muted: "#8b95a8", faint: "#5d6678",
      create: "#4fd1c5", createHover: "#6fdcd2", createInk: "#0e1117", createText: "#6fdcd2", createDeep: "#147a6e",
      accent: "#7aa2ff", accentDeep: "#3e63dd", accentLine: "#5b7ce6", accentSoft: "#1d2a4d", accentText: "#93b4ff",
      task: "#c79bff", taskDeep: "#a674f0", taskSoft: "#2f2448", you: "#dc2f6b",
      scratch: "#2b281b", scratchRule: "#3c3826",
    },
  },
];

const KEYS: Record<keyof Tones, string[]> = {
  sidebar: ["--sidebar"], list: ["--list"], paper: ["--paper"], hover: ["--hover", "--active"], line: ["--line"], chip: ["--chip"], input: ["--input"],
  ink: ["--ink"], ink2: ["--ink-2"], muted: ["--muted"], faint: ["--faint"],
  // The brand clay follows the theme's create colour (caret, active nav icon, tab underline).
  create: ["--create"], clay: ["--clay"], createHover: ["--create-hover"], createInk: ["--create-ink"], createText: ["--clay-text"], createDeep: ["--clay-deep"],
  accent: ["--accent"], accentDeep: ["--accent-deep"], accentLine: ["--accent-line"], accentSoft: ["--accent-soft"], accentText: ["--accent-text"],
  task: ["--task"], taskDeep: ["--task-deep"], taskSoft: ["--task-soft"], you: ["--you"],
  scratch: ["--scratch"], scratchRule: ["--scratch-rule"],
};

export const ALL_VARS = [...Object.values(KEYS).flat(), ...Object.keys(LIGHT_EXTRA)];

export function paletteVars(id: PaletteId, dark: boolean): Record<string, string> | null {
  const p = PALETTES.find((x) => x.id === id);
  const tones = p && (dark ? p.dark : p.light);
  if (!tones) return null;
  const out: Record<string, string> = { ...(dark ? DARK_EXTRA : LIGHT_EXTRA) };
  for (const [k, vars] of Object.entries(KEYS) as [keyof Tones, string[]][]) for (const v of vars) out[v] = tones[k] ?? tones.create;
  return out;
}

/** Applies a theme to <html>; "engram" (or an unknown id) falls back to the stylesheet. */
export function applyPalette(id: PaletteId, dark: boolean, theme: "system" | "light" | "dark" = "system") {
  const root = document.documentElement;
  const vars = paletteVars(id, dark);
  for (const v of ALL_VARS) root.style.removeProperty(v);
  if (vars) for (const [k, val] of Object.entries(vars)) root.style.setProperty(k, val);
  root.dataset.palette = id;
  // public/theme-boot.js paints this before the app loads next time (both modes, so System can switch).
  try {
    localStorage.setItem("engram.themeVars", JSON.stringify({ id, theme, light: paletteVars(id, false), dark: paletteVars(id, true) }));
  } catch {
    /* no storage */
  }
  // The browser / Android status bar colour follows the sidebar.
  const bar = vars?.["--sidebar"] ?? getComputedStyle(root).getPropertyValue("--sidebar").trim();
  for (const m of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) m.content = bar;
}
